import {Tabs, TabsContent, TabsList, TabsTrigger} from "@sakura-grp/ui/tabs";
import {Plus} from "lucide-react";
import {BaseTabContent} from "./dashboard/base-tab-content";
import {CustomerListToolbar} from "./dashboard/customer-list-toolbar";
import {BusinessCardCreateSheet} from "./dashboard/business-card-create-sheet";
import {CustomerListSkeleton} from "@/components/ui/skeleton/customer-list-skeleton";
import {useCallback, useState, type RefObject} from "react";
import {usePullToReload} from "@/hooks/usePullToReload";
import { useScrollRestoration } from '@/hooks/useScrollRestoration';
import { PullToRefreshIndicator } from '@/components/ui/PullToRefreshIndicator';
import { withMinDuration } from '@/lib/utils';
import { useHostessStore } from '@/stores/hostess';
import { useUIStore } from '@/stores/ui';
import { getHostessProfileImageUrlOrPlaceholder, getStoreLogoUrl } from '@/lib/storage-utils';
import { useQueryState, parseAsStringEnum } from 'nuqs';
import { useTabCustomers } from '@/hooks/useTabCustomers';
import { useTabCounts } from '@/hooks/useTabCounts';
import { InfiniteScrollSentinel } from '@/components/shared/InfiniteScrollSentinel';

// タブの定義
const TABS = ["cards", "assigned", "branch", "others", "store", "non-members"] as const;
type TabId = (typeof TABS)[number];
const tabValues: TabId[] = [...TABS];

const TAB_EMPTY_MESSAGES: Record<TabId, { message: string; description: string }> = {
  cards: { message: "顧客がいません", description: "まだ顧客が登録されていません。名刺を追加して顧客を登録しましょう。" },
  assigned: { message: "担当顧客 0名", description: "" },
  branch: { message: "枝客 0名", description: "" },
  others: { message: "他の担当 0名", description: "" },
  store: { message: "店客 0名", description: "" },
  "non-members": { message: "非会員 0名", description: "" },
};

export const AppHostssesDashboardPage = () => {
  // タブ状態をURLと同期
  const [activeTab, setActiveTab] = useQueryState(
    'tab',
    parseAsStringEnum(tabValues)
      .withDefault('cards')
      .withOptions({ clearOnDefault: true })
  );

  // ログイン中のホステス情報を取得
  const { hostess } = useHostessStore();

  // ホステスのプロフィール画像と所属店舗ロゴを動的に取得
  const profileImageUrl = getHostessProfileImageUrlOrPlaceholder(hostess?.profile_image_path);
  const currentStore = hostess?.current_assignment?.[0]?.store;
  const storeLogoUrl = getStoreLogoUrl(currentStore?.logo_path);

  // フィルター状態を取得
  const { filters } = useUIStore();

  // 名刺登録 drawer
  const [createCardOpen, setCreateCardOpen] = useState(false);

  // タブ件数を取得
  const { data: tabCounts, refetch: refetchCounts } = useTabCounts();

  // アクティブタブの顧客データを取得
  const {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useTabCustomers(activeTab, {
    search: filters.search,
    statusTypes: filters.statusTypes,
    selectedStoreIds: filters.selectedStoreIds,
    dateRange: filters.dateRange,
  });

  // Pull to Reload
  const {ref, refreshing, isReady} = usePullToReload({
    onRefresh: () => withMinDuration(async () => {
      await Promise.all([refetch(), refetchCounts()]);
    }),
    onError: (error) => {
      console.error("顧客データの更新に失敗しました:", error);
    }
  });

  // スクロール位置復元
  useScrollRestoration(ref as RefObject<HTMLElement>);

  // 全ページの顧客をフラットに結合
  const allCustomers = data?.pages.flatMap(page => page.customers) ?? [];

  const totalRows = data?.pages[0]?.totalRows ?? 0;

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const emptyMsg = TAB_EMPTY_MESSAGES[activeTab as TabId] ?? TAB_EMPTY_MESSAGES.cards;

  // 店舗フィルターが全解除されているかどうか（storeFilterInitialized === true の前提）
  const noStoreSelected = useUIStore((s) => s.storeFilterInitialized) && filters.selectedStoreIds.length === 0;

  return (
    <main className="relative h-full flex flex-col">
      <div
        ref={ref as any}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [touch-action:pan-y] [-webkit-overflow-scrolling:touch] bg-white"
      >
        {/* コンテンツラッパー：CSS変数でインジケーターとコンテンツを一緒に移動。
            pb は FAB（名刺を登録）が最終行を隠さないためのクリアランス */}
        <div
          className="min-h-full will-change-transform pb-20"
          style={{
            transform: `translateY(var(--pull-offset, 0px))`,
            transition: 'var(--pull-transition, none)'
          }}
        >
          {/* インジケーター（上に隠れている） */}
          <PullToRefreshIndicator refreshing={refreshing} isReady={isReady} />

        <div className="gap-md px-lg pt-sm relative flex items-center justify-center pb-[1.5rem]">
        <div className="gap-md me-auto flex items-center">
          <figure data-slot="avatar" className="relative flex size-[4rem] flex-none overflow-hidden rounded border border-gray-300 bg-brand/10">
            <img src={profileImageUrl} alt={hostess?.name || ""} className="w-full h-full object-cover" />
          </figure>
          <h2 className="truncate text-3xl font-bold leading-snug">
            <span className="gap-xs text-(length:--smaller-lg) flex items-center">
              <span className="size-md block rounded-full bg-green-400"></span>
              <span className="font-bold text-green-500">{hostess?.role || 'ホステス'}</span>
            </span>
            {hostess?.name || '読み込み中...'}
          </h2>
        </div>
        <img src={storeLogoUrl} alt={currentStore?.store_name || "店舗ロゴ"} className="w-[8rem]" />
      </div>

      {error && (
        <div className="px-lg py-md text-center text-red-500">
          顧客データの読み込みに失敗しました
        </div>
      )}

      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as TabId)} className="w-full">
        <TabsList className="gap-xs px-md text-lg">
          <TabsTrigger value="cards">名刺</TabsTrigger>
          <TabsTrigger value="assigned">🤵‍♂️ 担{tabCounts?.assigned != null ? ` (${tabCounts.assigned})` : ''}</TabsTrigger>
          <TabsTrigger value="branch">🪾 枝</TabsTrigger>
          <TabsTrigger value="others">🙅 他</TabsTrigger>
          <TabsTrigger value="store">🏛️ 店</TabsTrigger>
          <TabsTrigger value="non-members">👤 非</TabsTrigger>
        </TabsList>

        {TABS.map((tab) => (
          <TabsContent key={tab} value={tab}>
            <CustomerListToolbar />
            {noStoreSelected ? (
              <div className="py-xl px-lg flex flex-col items-center justify-center text-center">
                <p className="text-base font-semibold text-stone-700">店舗を選択してください</p>
                <p className="mt-sm text-sm text-stone-500">右上のチェックボックスから絞り込む店舗を選択してください</p>
              </div>
            ) : isLoading ? (
              <CustomerListSkeleton />
            ) : (
              <>
                <BaseTabContent
                  customers={allCustomers}
                  emptyMessage={emptyMsg.message}
                  emptyDescription={emptyMsg.description}
                />
                <InfiniteScrollSentinel
                  onLoadMore={handleLoadMore}
                  hasNextPage={!!hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                />
                {totalRows > 0 && (
                  <div className="py-2 text-center text-xs text-gray-400">
                    {allCustomers.length} / {totalRows} 件表示
                  </div>
                )}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>
        </div>
      </div>

      {/* 名刺登録 FAB: pull-to-refresh の transform コンテナの外（main 基準の absolute）に
          置くこと。transform サブツリー内の fixed は viewport 基準にならず、引っ張り更新中に
          一緒に動いてしまう */}
      <button
        type="button"
        onClick={() => setCreateCardOpen(true)}
        className="bottom-md right-md gap-sm px-lg absolute z-10 flex h-12 items-center rounded-full bg-stone-800 font-bold text-white shadow-lg transition-transform motion-safe:active:scale-95"
      >
        <Plus className="size-5" />
        名刺を登録
      </button>
      <BusinessCardCreateSheet open={createCardOpen} onOpenChange={setCreateCardOpen} />
    </main>
  );
};
